import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { common, appointment } from "src/types/index";

const initialState: appointment.AppointmentStateType = {
  appointmentList: {
    isLoading: false,
    data: [],
    error: "",
    isLastPageReached: false,
  },
  appointmentTimeSlot: {
    isLoading: false,
    data: [],
    error: "",
    consultationBookingBlocked: null,
  },
};
const appointmentSlice = createSlice({
  name: "appointment",
  initialState,
  reducers: {
    getAppointmentListRequestAction: (
      state: appointment.AppointmentStateType,
      _action: PayloadAction<any>
    ) => {
      state.appointmentList.isLoading = true;
      state.appointmentList.error = "";
      if (
        Number(_action.payload.payload.page) === 1 &&
        state.appointmentList.isLastPageReached
      ) {
        state.appointmentList.isLastPageReached = false;
      }
    },
    getAppointmentListSuccessAction: (
      state: appointment.AppointmentStateType,
      action: PayloadAction<common.ResponseGenerator>
    ) => {
      state.appointmentList.isLoading = false;
      if (Number(action.payload.code) === 1) {
        state.appointmentList.isLastPageReached =
          action.payload.data.appointment_list.length < 5;
        if (Number(action.payload?.page) === 1) {
          state.appointmentList.data = [
            ...action.payload.data.appointment_list,
          ];
        } else if (Number(action.payload?.page) > 1) {
          state.appointmentList.data = [
            ...state.appointmentList.data,
            ...action.payload.data.appointment_list,
          ];
        }
      } else {
        state.appointmentList.isLastPageReached = true;
      }
      state.appointmentList.error = "";
    },
    getAppointmentListErrorAction: (
      state: appointment.AppointmentStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.appointmentList.isLoading = false;
      state.appointmentList.data = [];
      state.appointmentList.error = error;
    },
    cancelAppointmentRequest: (
      state: appointment.AppointmentStateType,
      _action: PayloadAction<any>
    ) => {
      state.appointmentList.isLoading = true;
      state.appointmentList.error = "";
    },
    cancelAppointmentSuccess: (
      state: appointment.AppointmentStateType,
      action: PayloadAction<common.ResponseGenerator>
    ) => {
      state.appointmentList.isLoading = false;
      state.appointmentList.error = "";
    },
    cancelAppointmentError: (
      state: appointment.AppointmentStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.appointmentList.isLoading = false;
      state.appointmentList.error = error;
    },
    getTimeSlotRequestAction: (
      state: appointment.AppointmentStateType,
      _action: PayloadAction<any>
    ) => {
      state.appointmentTimeSlot.isLoading = true;
      state.appointmentTimeSlot.error = "";
      state.appointmentTimeSlot.data = [];
      state.appointmentTimeSlot.consultationBookingBlocked = null;
    },
    getTimeSlotSuccessAction: (
      state: appointment.AppointmentStateType,
      action: PayloadAction<common.ResponseGenerator>
    ) => {
      state.appointmentTimeSlot.isLoading = false;
      state.appointmentTimeSlot.consultationBookingBlocked = null;
      if (Number(action.payload.code) === 1) {
        state.appointmentTimeSlot.data =
          action.payload.data.result?.time_slot ?? [];
      }
    },
    getTimeSlotConsultationBlockedAction: (
      state: appointment.AppointmentStateType,
      action: PayloadAction<common.ResponseGenerator>
    ) => {
      state.appointmentTimeSlot.isLoading = false;
      state.appointmentTimeSlot.error = "";
      state.appointmentTimeSlot.data = [];
      const data = action.payload.data;
      const message =
        (action.payload.message ??
          (typeof data?.userMessage === "string" ? data.userMessage : "")) ||
        "";
      const highlightText =
        typeof data?.highlightText === "string" &&
        data.highlightText.length > 0
          ? data.highlightText
          : undefined;
      state.appointmentTimeSlot.consultationBookingBlocked = {
        message,
        blockReasonCode:
          typeof data?.blockReasonCode === "string"
            ? data.blockReasonCode
            : undefined,
        ...(highlightText
          ? {
              messageBefore:
                typeof data?.messageBefore === "string"
                  ? data.messageBefore
                  : "",
              highlightText,
              messageAfter:
                typeof data?.messageAfter === "string"
                  ? data.messageAfter
                  : "",
            }
          : {}),
      };
    },
    getTimeSlotErrorAction: (
      state: appointment.AppointmentStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.appointmentTimeSlot.isLoading = false;
      state.appointmentTimeSlot.error = error;
      state.appointmentTimeSlot.consultationBookingBlocked = null;
    },
    addAppointmentRequestAction: (
      state: appointment.AppointmentStateType,
      _action: PayloadAction<any>
    ) => {
      state.appointmentTimeSlot.isLoading = true;
      state.appointmentTimeSlot.error = "";
      state.appointmentTimeSlot.consultationBookingBlocked = null;
    },
    addAppointmentSuccessAction: (
      state: appointment.AppointmentStateType,
      action: PayloadAction<common.ResponseGenerator>
    ) => {
      state.appointmentTimeSlot.isLoading = false;
      state.appointmentTimeSlot.consultationBookingBlocked = null;
    },
    addAppointmentConsultationBlockedAction: (
      state: appointment.AppointmentStateType,
      action: PayloadAction<common.ResponseGenerator>
    ) => {
      state.appointmentTimeSlot.isLoading = false;
      state.appointmentTimeSlot.error = "";
      const data = action.payload.data;
      const message =
        (action.payload.message ??
          (typeof data?.userMessage === "string" ? data.userMessage : "")) ||
        "";
      const highlightText =
        typeof data?.highlightText === "string" &&
        data.highlightText.length > 0
          ? data.highlightText
          : undefined;
      state.appointmentTimeSlot.consultationBookingBlocked = {
        message,
        blockReasonCode:
          typeof data?.blockReasonCode === "string"
            ? data.blockReasonCode
            : undefined,
        ...(highlightText
          ? {
              messageBefore:
                typeof data?.messageBefore === "string"
                  ? data.messageBefore
                  : "",
              highlightText,
              messageAfter:
                typeof data?.messageAfter === "string"
                  ? data.messageAfter
                  : "",
            }
          : {}),
      };
    },
    addAppointmentErrorAction: (
      state: appointment.AppointmentStateType,
      { payload: error }: PayloadAction<string>
    ) => {
      state.appointmentTimeSlot.isLoading = false;
      state.appointmentTimeSlot.error = error;
      state.appointmentTimeSlot.consultationBookingBlocked = null;
    },
  },
});

export const {
  getAppointmentListRequestAction,
  getAppointmentListSuccessAction,
  getAppointmentListErrorAction,

  cancelAppointmentRequest,
  cancelAppointmentSuccess,
  cancelAppointmentError,

  getTimeSlotRequestAction,
  getTimeSlotSuccessAction,
  getTimeSlotConsultationBlockedAction,
  getTimeSlotErrorAction,

  addAppointmentRequestAction,
  addAppointmentSuccessAction,
  addAppointmentConsultationBlockedAction,
  addAppointmentErrorAction,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;
